import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGS, setLanguage } from "@/i18n";

export default function LanguageSelector({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  return (
    <div ref={ref} className="relative" data-testid="language-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("language.select")}
        data-testid="language-toggle"
        className={`inline-flex items-center gap-1.5 rounded-full border border-black/10 text-iyaf-ink/75 hover:text-iyaf-ink hover:bg-iyaf-paper transition ${
          compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs"
        }`}
      >
        <Globe size={compact ? 12 : 14} />
        <span className="font-semibold uppercase tracking-widest">{current.code}</span>
        <ChevronDown size={compact ? 10 : 12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute end-0 mt-2 w-52 bg-white rounded-2xl border border-black/5 shadow-2xl shadow-black/10 p-1 z-50" data-testid="language-menu">
          {SUPPORTED_LANGS.map((l) => {
            const isActive = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                data-testid={`language-option-${l.code}`}
                onClick={() => { setLanguage(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                  isActive ? "bg-iyaf-paper text-iyaf-ink font-semibold" : "text-iyaf-ink/75 hover:bg-iyaf-paper"
                }`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="flex-1 text-start">{l.native}</span>
                {isActive && <Check size={14} className="text-iyaf-blue" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
